import type { LensCard, LensCardLayout, LensCardType } from '~/types/leLens';

type CardFrame = LensCardLayout & { i: string };

const CHART_TYPES: ReadonlyArray<LensCardType> = [
  'line',
  'bar',
  'drivers',
  'stacked',
  'area',
  'scatter',
  'pie',
  'combo',
  'heatmap',
  'boxplot',
];

export type ChartWidthGesture = { expandedChartIds: Array<string>; widthResizedCardId?: string };

/** Vacant columns beside the complete card, bounded by the next saved frame. */
export function contiguousFreeColumnsRight(item: CardFrame, layout: ReadonlyArray<CardFrame>, columns: number): number {
  const right = item.x + item.w;
  let stop = columns;
  for (const other of layout) {
    if (other.i === item.i || other.x < right) continue;
    if (other.y < item.y + item.h && other.y + other.h > item.y) stop = Math.min(stop, other.x);
  }
  return Math.max(0, stop - right);
}

/**
 * Give an automatic chart the vacant columns formerly used by its commentary.
 * The grid owns this displayed frame so its resize edge matches the card edge.
 * Saved frames and deliberately sized cards remain untouched.
 */
export function expandAutomaticChartFrames<T extends CardFrame>(
  cards: ReadonlyArray<LensCard>,
  layout: ReadonlyArray<T>,
  columns: number,
): Array<T> {
  const byId = new Map(cards.map((card) => [card.id, card]));
  return layout.map((item) => {
    const card = byId.get(item.i);
    if (!card || !CHART_TYPES.includes(card.type) || card.layout?.by === 'user') return item;
    const free = contiguousFreeColumnsRight(item, layout, columns);
    return free >= 3 ? { ...item, w: item.w + free } : item;
  });
}

/** Discard display-only width expansion when a gesture has not resized that width. */
export function restoreAutomaticChartWidths(
  next: ReadonlyArray<CardFrame>,
  source: ReadonlyArray<CardFrame>,
  expanded: ReadonlyArray<CardFrame>,
  widthResizedCardId?: string,
): Record<string, LensCardLayout> {
  const sourceById = new Map(source.map((item) => [item.i, item]));
  const expandedById = new Map(expanded.map((item) => [item.i, item]));
  return Object.fromEntries(
    next.map((item) => {
      const saved = sourceById.get(item.i);
      const displayed = expandedById.get(item.i);
      const w =
        saved && displayed && displayed.w !== saved.w && item.i !== widthResizedCardId && item.w === displayed.w
          ? saved.w
          : item.w;
      return [item.i, { x: item.x, y: item.y, w, h: item.h }];
    }),
  );
}

/** Moving or vertically resizing a chart keeps its automatic width treatment. */
export function preserveChartWidthModes(
  cards: ReadonlyArray<LensCard>,
  frames: Record<string, LensCardLayout>,
  gesture?: ChartWidthGesture,
): Record<string, LensCardLayout> {
  if (!gesture) return frames;
  const expanded = new Set(gesture.expandedChartIds);
  const byId = new Map(cards.map((card) => [card.id, card]));
  return Object.fromEntries(
    Object.entries(frames).map(([id, frame]) => {
      const card = byId.get(id);
      if (card && expanded.has(id) && id === gesture.widthResizedCardId) return [id, { ...frame, by: 'user' }];
      if (!card || !expanded.has(id) || card.layout?.by === 'user') return [id, frame];
      return [id, { ...frame, by: card.layout?.by ?? 'auto' }];
    }),
  );
}
